/**
 * Aba Anúncios: uma linha por anúncio em campanha, com as DUAS conversões lado a lado (pedido dela, 09/10/2026):
 * conversão do Ads (vendas atribuídas ÷ cliques) e conversão do anúncio inteiro (pedidos da `margens` ÷ visitas).
 * Ordem das colunas igual à aba Campanhas (pedido dela, 20/10/2026): Cliques → Vendas → Conv. Ads → Conv. anúncio
 * → CPC → Unidades vendidas → Custo por item vendido; a primeira linha é o subtotal do que está na lista.
 */
import { memo, useMemo, useState } from 'react'
import { Box, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material'
import { brl, inteiro, nomeConta, pct, somar, STATUS_ADS_LABEL, type AnuncioLinha, type Somatorio } from './logic'
import { Paginacao, Th, Vazio, useOrdenacao } from './ui'

const F12 = { fontSize: 12 } as const
const F12B = { fontSize: 12, fontWeight: 600 } as const

const Linha = memo(function Linha({ l }: { l: AnuncioLinha }) {
  const semVisita = l.conversao_anuncio == null
  return (
    <TableRow hover>
      <TableCell sx={{ maxWidth: 280 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }} noWrap title={l.titulo}>{l.titulo}</Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          <a href={`https://produto.mercadolivre.com.br/${l.item_id.replace(/^MLB/, 'MLB-')}`} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{l.item_id}</a>
          {' · '}{nomeConta(l.user_id)}{l.price != null ? ` · ${brl(l.price)}` : ''}
        </Typography>
      </TableCell>
      <TableCell sx={{ fontSize: 12, maxWidth: 180 }}><Typography noWrap sx={F12} title={l.campanha_nome || ''}>{l.campanha_nome || (l.campaign_id ? `#${l.campaign_id}` : '—')}</Typography></TableCell>
      <TableCell sx={F12}>
        <Chip size="small" label={STATUS_ADS_LABEL[l.status_ads || ''] || l.status_ads || '—'} color={l.status_ads === 'hold' ? 'warning' : l.status_ads === 'active' ? 'success' : 'default'} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
      </TableCell>
      <TableCell align="right" sx={F12B}>{brl(l.cost)}</TableCell>
      <TableCell align="right" sx={F12}>{brl(l.total_amount)}</TableCell>
      <TableCell align="right" sx={F12}>{pct(l.acos)}</TableCell>
      <TableCell align="right" sx={F12}>{inteiro(l.clicks)}</TableCell>
      <TableCell align="right" sx={F12}>{inteiro(l.itens)}</TableCell>
      <TableCell align="right" sx={F12B}>{pct(l.conversao_ads, 2)}</TableCell>
      <TableCell align="right" sx={F12B}>
        {semVisita ? <Tooltip title={l.motivo || 'sem visitas no período'}><span>—</span></Tooltip> : pct(l.conversao_anuncio, 2)}
      </TableCell>
      <TableCell align="right" sx={F12}>{l.visitas == null ? <Tooltip title="sem visitas coletadas"><span>—</span></Tooltip> : inteiro(l.visitas)}</TableCell>
      <TableCell align="right" sx={F12}>{inteiro(l.pedidos)}</TableCell>
      <TableCell align="right" sx={F12}>{brl(l.cpc)}</TableCell>
      <TableCell align="right" sx={F12}>{inteiro(l.unidades)}</TableCell>
      <TableCell align="right" sx={F12}>{brl(l.custo_unidade)}</TableCell>
      <TableCell align="right" sx={F12}>{brl(l.faturamento)}</TableCell>
    </TableRow>
  )
})

const LinhaSubtotal = memo(function LinhaSubtotal({ s, faturamento }: { s: Somatorio; faturamento: number }) {
  const sx = { fontSize: 12, fontWeight: 700, bgcolor: 'action.hover', borderBottom: '2px solid', borderBottomColor: 'divider' } as const
  return (
    <TableRow>
      <TableCell sx={sx}>Subtotal · {s.linhas} anúncio(s)</TableCell>
      <TableCell sx={sx} />
      <TableCell sx={sx} />
      <TableCell align="right" sx={sx}>{brl(s.cost)}</TableCell>
      <TableCell align="right" sx={sx}>{brl(s.total_amount)}</TableCell>
      <TableCell align="right" sx={sx}>{pct(s.acos)}</TableCell>
      <TableCell align="right" sx={sx}>{inteiro(s.clicks)}</TableCell>
      <TableCell align="right" sx={sx}>{inteiro(s.itens)}</TableCell>
      <TableCell align="right" sx={sx}>{pct(s.conversao, 2)}</TableCell>
      <TableCell align="right" sx={sx}>{s.visitas == null ? <Tooltip title="nenhum anúncio da lista tem visita coletada"><span>—</span></Tooltip> : pct(s.conversao_anuncio, 2)}</TableCell>
      <TableCell align="right" sx={sx}>{s.visitas == null ? '—' : inteiro(s.visitas)}</TableCell>
      <TableCell align="right" sx={sx}>{inteiro(s.pedidos)}</TableCell>
      <TableCell align="right" sx={sx}>{brl(s.cpc)}</TableCell>
      <TableCell align="right" sx={sx}>{inteiro(s.unidades)}</TableCell>
      <TableCell align="right" sx={sx}>{brl(s.custo_unidade)}</TableCell>
      <TableCell align="right" sx={sx}>{brl(faturamento)}</TableCell>
    </TableRow>
  )
})

export function AbaAnuncios({ linhas, campanhaFiltro, onLimparCampanha }: { linhas: AnuncioLinha[]; campanhaFiltro: { id: number; nome: string | null } | null; onLimparCampanha: () => void }) {
  const [busca, setBusca] = useState('')
  const filtradas = useMemo(() => {
    const b = busca.trim().toLowerCase()
    return b ? linhas.filter((l) => l.item_id.toLowerCase().includes(b) || l.titulo.toLowerCase().includes(b) || (l.campanha_nome || '').toLowerCase().includes(b)) : linhas
  }, [linhas, busca])
  const subtotal = useMemo(() => somar(filtradas), [filtradas])
  const faturamento = useMemo(() => Math.round(filtradas.reduce((x, l) => x + (l.faturamento || 0), 0) * 100) / 100, [filtradas])
  const ord = useOrdenacao<AnuncioLinha>(filtradas, 'cost')

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <TextField size="small" label="Buscar MLB, título ou campanha" value={busca} onChange={(e) => setBusca(e.target.value)} sx={{ minWidth: 280 }} />
        {campanhaFiltro ? <Chip size="small" color="primary" label={`campanha: ${campanhaFiltro.nome || `#${campanhaFiltro.id}`}`} onDelete={onLimparCampanha} /> : null}
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          Conv. Ads = vendas atribuídas ÷ cliques · Conv. anúncio = pedidos (base margens) ÷ visitas (API do ML), anúncio inteiro
        </Typography>
      </Stack>
      {!linhas.length ? <Vazio texto="Nenhum anúncio em campanha para esta loja e período." /> : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <Th<AnuncioLinha> k="titulo" label="Anúncio" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} align="left" />
                  <Th<AnuncioLinha> k="campanha_nome" label="Campanha" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} align="left" />
                  <Th<AnuncioLinha> k="status_ads" label="Status Ads" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} align="left" hint="hold = travado por estoque ou pausa no marketplace" />
                  <Th<AnuncioLinha> k="cost" label="Gasto" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} />
                  <Th<AnuncioLinha> k="total_amount" label="Venda atribuída" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Direta + indireta, em R$" />
                  <Th<AnuncioLinha> k="acos" label="ACOS" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} />
                  <Th<AnuncioLinha> k="clicks" label="Cliques" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} />
                  <Th<AnuncioLinha> k="itens" label="Vendas" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Vendas atribuídas ao Ads (direta + indireta), em quantidade" />
                  <Th<AnuncioLinha> k="conversao_ads" label="Conv. Ads" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Vendas ÷ cliques" />
                  <Th<AnuncioLinha> k="conversao_anuncio" label="Conv. anúncio" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Anúncio inteiro (Ads + orgânico): pedidos ÷ visitas, no mesmo período. A API do Meli não separa visita orgânica de paga." />
                  <Th<AnuncioLinha> k="visitas" label="Visitas" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} />
                  <Th<AnuncioLinha> k="pedidos" label="Pedidos" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Base margens (type pedido), cada MLB do pedido conta" />
                  <Th<AnuncioLinha> k="cpc" label="CPC" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Gasto ÷ cliques" />
                  <Th<AnuncioLinha> k="unidades" label="Unidades vendidas" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Unidades atribuídas ao Ads (direta + indireta)" />
                  <Th<AnuncioLinha> k="custo_unidade" label="Custo por item vendido" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Gasto ÷ unidades vendidas" />
                  <Th<AnuncioLinha> k="faturamento" label="Faturamento" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Só pedidos com um anúncio; pedidos com vários MLBs não são rateados" />
                </TableRow>
              </TableHead>
              <TableBody>
                {ord.ordenadas.length ? <LinhaSubtotal s={subtotal} faturamento={faturamento} /> : null}
                {ord.visiveis.map((l) => <Linha key={`${l.user_id}-${l.item_id}`} l={l} />)}
              </TableBody>
            </Table>
          </Box>
          <Paginacao total={ord.ordenadas.length} pagina={ord.pagina} setPagina={ord.setPagina} />
        </>
      )}
    </Box>
  )
}
